import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

const EXPAND_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
  '<polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>' +
  '<polyline points="21 3 14 10"/><polyline points="3 21 10 14"/></svg>';

const COLLAPSE_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
  '<polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>' +
  '<polyline points="14 10 21 3"/><polyline points="10 14 3 21"/></svg>';

const CSS_FS_CLASS = "c-routedetail-leaflet-map--css-fullscreen";

function isFullscreenSupported() {
  return !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);
}

function requestFullscreen(el) {
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen();
}

function exitFullscreen() {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
}

function getFullscreenElement() {
  return document.fullscreenElement || document.webkitFullscreenElement;
}

const FULLSCREEN_CHANGE_EVENT = document.webkitFullscreenEnabled
  ? "webkitfullscreenchange"
  : "fullscreenchange";

const FullscreenControl = () => {
  const map = useMap();
  const buttonRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const container = map.getContainer();
    const supportsNative = isFullscreenSupported();

    function updateIcon(isFs) {
      if (buttonRef.current) {
        buttonRef.current.innerHTML = isFs ? COLLAPSE_ICON : EXPAND_ICON;
        buttonRef.current.title = isFs ? "Fullscreen sluiten" : "Fullscreen";
      }
    }

    function isCssFullscreen() {
      return container.classList.contains(CSS_FS_CLASS);
    }

    function toggleCssFullscreen() {
      const entering = !isCssFullscreen();
      container.classList.toggle(CSS_FS_CLASS);
      document.body.style.overflow = entering ? "hidden" : "";
      updateIcon(entering);
      timerRef.current = setTimeout(() => map.invalidateSize(), 100);
    }

    function toggleFullscreen(e) {
      L.DomEvent.stop(e);
      if (supportsNative) {
        if (getFullscreenElement()) {
          exitFullscreen();
        } else {
          requestFullscreen(container);
        }
      } else {
        toggleCssFullscreen();
      }
    }

    function onFullscreenChange() {
      const isFs = !!getFullscreenElement();
      updateIcon(isFs);
      timerRef.current = setTimeout(() => map.invalidateSize(), 100);
    }

    function onKeyDown(e) {
      if (e.key === "Escape" && isCssFullscreen()) {
        toggleCssFullscreen();
      }
    }

    const control = L.control({ position: "topleft" });

    control.onAdd = function () {
      const wrapper = L.DomUtil.create(
        "div",
        "leaflet-bar leaflet-control leaflet-control-fullscreen"
      );
      const link = L.DomUtil.create(
        "a",
        "leaflet-control-fullscreen-button",
        wrapper
      );
      link.href = "#";
      link.role = "button";
      link.title = "Fullscreen";
      link.innerHTML = EXPAND_ICON;
      buttonRef.current = link;

      L.DomEvent.on(link, "click", toggleFullscreen);
      L.DomEvent.disableClickPropagation(wrapper);
      return wrapper;
    };

    map.addControl(control);

    if (supportsNative) {
      document.addEventListener(FULLSCREEN_CHANGE_EVENT, onFullscreenChange);
    } else {
      document.addEventListener("keydown", onKeyDown);
    }

    return () => {
      map.removeControl(control);
      if (supportsNative) {
        document.removeEventListener(
          FULLSCREEN_CHANGE_EVENT,
          onFullscreenChange
        );
      } else {
        document.removeEventListener("keydown", onKeyDown);
      }
      if (timerRef.current) clearTimeout(timerRef.current);
      if (isCssFullscreen()) {
        container.classList.remove(CSS_FS_CLASS);
        document.body.style.overflow = "";
      }
    };
  }, [map]);

  return null;
};

export default FullscreenControl;
